import React from 'react'
import { useAppStore } from '../lib/store'
import { Users, UserPlus, Search, Filter, MoreVertical, Mail, Calendar, Shield, AlertCircle, CheckCircle, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { toast } from '../lib/toast'
import UserModal from './UserModal'
import type { User as UserType } from '../types'

export default function UserManagement() {
  const { user: currentUser, users, addUser, updateUser, deleteUser, getRealUsers } = useAppStore()
  const [loading, setLoading] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [roleFilter, setRoleFilter] = React.useState<string>('all')
  const [statusFilter, setStatusFilter] = React.useState<string>('all')
  const [sortBy, setSortBy] = React.useState<'name' | 'email' | 'role' | 'created_at' | 'status'>('created_at')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [selectedUser, setSelectedUser] = React.useState<UserType | null>(null)
  const [showUserModal, setShowUserModal] = React.useState(false)
  const [modalMode, setModalMode] = React.useState<'view' | 'edit' | 'create'>('view')

  React.useEffect(() => {
    setLoading(false)
  }, [])

  // Role-based filtering - only admin can see all users
  const getFilteredUsers = () => {
    if (currentUser?.role !== 'admin') {
      // Non-admin users can only see themselves
      return users.filter(user => user.id === currentUser?.id)
    }
    
    // Check if this is a real admin or demo admin
    const demoAdminEmails = ['sarah.j@email.com', 'mike.w@email.com', 'emily.d@email.com', 'robert.b@email.com', 'lisa.a@email.com', 'admin@demo.com', 'test@admin.com'];
    const isDemoAdmin = currentUser && demoAdminEmails.includes(currentUser.email);
    
    // Use real data for real admins, all data for demo admins
    const usersData = isDemoAdmin ? users : getRealUsers();
    
    console.log('UserManagement - User email:', currentUser?.email);
    console.log('UserManagement - Is demo admin:', isDemoAdmin);
    console.log('UserManagement - Total users before filter:', users.length);
    console.log('UserManagement - Real users after filter:', getRealUsers().length);
    
    return usersData
  }

  // Sort functionality
  const sortUsers = (usersToSort: UserType[]) => {
    const sorted = [...usersToSort].sort((a, b) => {
      let comparison = 0
      
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '')
          break
        case 'email':
          comparison = a.email.localeCompare(b.email)
          break
        case 'role':
          comparison = a.role.localeCompare(b.role)
          break
        case 'created_at':
          comparison = (a.created_at || '').localeCompare(b.created_at || '')
          break
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '')
          break
        default:
          comparison = 0
      }
      
      return sortOrder === 'asc' ? comparison : -comparison
    })
    
    return sorted
  }

  // Filter and sort users
  const filteredUsers = sortUsers(
    getFilteredUsers().filter(user => {
      const matchesSearch = (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.role.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = roleFilter === 'all' || user.role === roleFilter
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter
      return matchesSearch && matchesRole && matchesStatus
    })
  )

  const handleUserAction = (action: string, userId: string) => {
    const user = users.find(u => u.id === userId)
    if (!user) return

    switch (action) {
      case 'View':
        setSelectedUser(user)
        setModalMode('view')
        setShowUserModal(true)
        break
      case 'Edit':
        setSelectedUser(user)
        setModalMode('edit')
        setShowUserModal(true)
        break
      case 'Create':
        setSelectedUser(null)
        setModalMode('create')
        setShowUserModal(true)
        break
      case 'Delete':
        if (confirm('Are you sure you want to delete this user?')) {
          deleteUser(userId)
          toast.success('User deleted successfully!')
        }
        break
      default:
        toast.info(`${action} user ${userId}`)
    }
  }

  const handleUserSave = (userData: UserType) => {
    if (modalMode === 'create') {
      addUser({ ...userData, id: Date.now().toString() })
      toast.success('User created successfully!')
    } else {
      updateUser(userData.id, userData)
      toast.success('User updated successfully!')
    }
    setShowUserModal(false)
  }

  const handleDeleteUser = (userId: string) => {
    deleteUser(userId)
    toast.success('User deleted successfully')
  }

  const handleStatusChange = (userId: string, newStatus: string) => {
    updateUser(userId, { status: newStatus as 'active' | 'inactive' })
    toast.success(`User status updated to ${newStatus}`)
  }

  const handleCreateUser = () => {
    setSelectedUser(null)
    setModalMode('create')
    setShowUserModal(true)
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
      case 'provider': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      case 'client': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      case 'pending': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
      case 'suspended': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />
      case 'pending': return <AlertCircle className="w-4 h-4" />
      case 'suspended': return <X className="w-4 h-4" />
      default: return <AlertCircle className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600 dark:text-dark-text-primary">Loading users...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Users className="w-6 h-6 text-purple-600" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">User Management</h1>
            </div>
            
            <button
              onClick={() => window.history.back()}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-lg transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="provider">Provider</option>
                <option value="client">Client</option>
              </select>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>

              <div className="flex items-center space-x-2">
                <ArrowUpDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'email' | 'role' | 'created_at' | 'status')}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                >
                  <option value="created_at">Sort by Date</option>
                  <option value="name">Sort by Name</option>
                  <option value="email">Sort by Email</option>
                  <option value="role">Sort by Role</option>
                  <option value="status">Sort by Status</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4 text-gray-600 dark:text-gray-400" /> : <ArrowDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
              Users ({filteredUsers.length})
            </h2>
            <button 
              onClick={handleCreateUser}
              className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors flex items-center space-x-2"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add User</span>
            </button>
          </div>
          
          <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
            <table className="w-full min-w-full md:table-auto">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-dark-border">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">User</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Role</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider hidden md:table-cell">Joined</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider hidden md:table-cell">Last Login</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-dark-border">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 md:space-x-3">
                        <div className="w-6 h-6 md:w-8 md:h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-300">
                            {(user.email || '').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.status || 'inactive')}`}>
                        {user.status || 'inactive'}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 dark:text-dark-text-muted hidden md:table-cell">
                      {new Date(user.created_at || '').toLocaleDateString()}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 dark:text-dark-text-muted hidden md:table-cell">
                      Never
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col md:flex-row md:items-center space-y-1 md:space-y-0 md:space-x-2">
                        <button
                          onClick={() => handleUserAction('View', user.id)}
                          className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 text-xs md:text-sm font-medium"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleUserAction('Edit', user.id)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 text-xs md:text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleUserAction('Suspend', user.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-xs md:text-sm font-medium"
                        >
                          Suspend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* User Modal */}
      <UserModal
        user={selectedUser}
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        onSave={handleUserSave}
        onDelete={handleDeleteUser}
        mode={modalMode}
      />
    </div>
  )
}
